import { Status } from "../utils/api";
import { createContext, useCallback, useContext, useState } from "react";
import { getDownloadURL, ref, uploadBytesResumable, UploadTask } from "firebase/storage";
import { db, storage } from "../firebase";
import { hash32, randomString, string_to_slug, trim } from "../utils/string";
import { useAuth } from "./useAuth";
import { addTrack, authUserToFsUser, Pack, Track, UITrack, updatePack, User } from "../firebase/firestore";
import { number } from "yup";
import FileUpload from "../components/upload/FileUpload";
import { DocumentReference, DocumentSnapshot, FieldValue, Firestore, updateDoc } from "firebase/firestore";
import { sum } from "../utils/arrays";

export const useUpload = (url: string) => {
  const [status, setStatus] = useState(Status.NOT_STARTED);
  const [progress, setProgress] = useState(0);

  const uploadFiles = (files: File[]) => {
    setStatus(Status.LOADING);

    const formData = new FormData();
    files.forEach((f) => formData.append(f.name, f));

    let request = new XMLHttpRequest();
    request.open("POST", url);

    // upload progress event
    request.upload.addEventListener("progress", function (e) {
      // upload progress as percentage
      let percent_completed = (e.loaded / e.total) * 100;
      setProgress(percent_completed);
    });

    // request finished event
    request.addEventListener("load", function (e) {
      if (request.status === 200) setStatus(Status.SUCCESS);
      else setStatus(Status.FAIL);

      // request.response holds response from the server
    });

    request.send(formData);
  };

  return { uploadFiles, status, progress };
};

const getUserStoragePath = (user) => `${user.uid}/`;

export const getPathWithoutExtension = (p) => p.replace(/\.[^/.]+$/, "");
export const getStorageFileName = (f: File) => {
  const fileTitle = getPathWithoutExtension(f.name);
  const fileExt = f.name.split(".").pop();
  return string_to_slug(fileTitle) + "_" + hash32(fileTitle + randomString(32), Date.now()) + "." + fileExt;
};

const extractBpm = (fname: string): [number, string] => {
  const re = /[^0-9]?(0?[3-9]\d|1\d\d|2[0-4]\d)(bpm|(?![ _-]*(bit|khz|mb|gb|kib))[^A-z0-9])/gi;
  const match = re.exec(fname);
  console.log(match);
  if (match?.length > 1) {
    const bpmStr = match[1];
    return [
      parseInt(bpmStr),
      fname
        .replace(bpmStr, "")
        .split(" ")
        .flatMap((s) => s.split("_"))
        .filter((part) => part.toLocaleLowerCase() !== "bpm")
        .join(" ")
        .replace(" bpm", "")
        .trimEnd(),
    ];
  }
  return [undefined, fname];
};

// key: c -> 0, b -> 11. mode: 0 = major, 1 = minor
export type Key = { key: string; mode: string };

const extractKey = (fname: string): [Key, string] => {
  const re = /([^A-z]|^)(([A-G]|[a-g])(#|b|♭|♯)?(m| ?minor| ?major)?)([^A-z]|$)/gi;
  const match = re.exec(fname);

  if (match && match.length > 3) {
    const keyStr = match[2];
    return [stringToKey(keyStr), fname];
  }
  return [undefined, fname];
};

// extracts relevant info from file name
const fileNameToTrack = (fname: string, analyzeFile = true): [any, string] => {
  let track = { file: {} };
  if (analyzeFile === false) return [track, fname];

  let [gotBpm, fnameNoBpm] = extractBpm(fname);
  let [gotKey, cleanFname] = extractKey(fnameNoBpm);

  if (gotBpm) {
    track.file["bpm"] = gotBpm;
    track["bpm"] = { source: "file", value: gotBpm };
  }
  if (gotKey) {
    track.file["key"] = gotKey;
    track["key"] = { source: "file", value: gotKey };
  }

  return [track, cleanFname];
};

export interface FileUpload {
  file: File;
  progress: number;
  error?: Error;
  track?: Track;
  tasks: UploadTask[];
}

export interface FirestoreTrack {
  ref: DocumentReference;
  track: Track;
  snapshot?: DocumentSnapshot;
}

const lcp = function (strs: string[]) {
  "use strict";
  if (strs === undefined || strs.length === 0) {
    return "";
  }

  return strs.reduce((prev, next) => {
    let i = 0;
    while (prev[i] && next[i] && prev[i] === next[i]) i++;
    return prev.slice(0, i);
  });
};

type FileNameGroup = {
  title: string;
  fnames: string[];
};

function groupFileNames(fnames: string[]): FileNameGroup[] {
  if (fnames.length == 0) return [];

  fnames.sort();

  let runningLcp = null; // "abc "
  let runningFnames = [fnames[0]]; // "abc "
  let groups = [];
  fnames.slice(0, fnames.length - 1).forEach((fname1, idx) => {
    let fname2 = fnames[idx + 1];
    let thisLcp = lcp([runningLcp || fname1, fname2]); // "abc "

    console.log(`${fname1}, ${fname2}\t(lcp: ${thisLcp}\trunningLcp: ${runningLcp})`);

    if (
      thisLcp === "" ||
      !(thisLcp.endsWith(" ") || thisLcp.endsWith("_")) ||
      (runningLcp !== null && thisLcp.length < runningLcp.length)
    ) {
      // group ended because no more lcp, or not the first pair and lcp got shorter
      let groupTitle = runningLcp || fname1;
      console.log(`dumping under ${groupTitle} \n`, runningFnames);
      groups.push({
        title: trim(trim(groupTitle, " "), "_"),
        fnames: runningFnames.map((str) => str.replace(groupTitle, "")),
      });
      runningLcp = null;
      runningFnames = [fname2];
    } else {
      // got a lcp and either first pair or lcp stayed
      runningLcp = thisLcp;
      runningFnames.push(fname2);
    }
  });

  groups.push({ title: runningLcp?.trim() || fnames[fnames.length - 1], fnames: runningFnames });

  return groups;
}

type StemFile = {
  name: string;
  file: File;
};

type TrackFile = {
  mainFile?: File;
  name: string;
  stems?: StemFile[];
};
function groupFiles(files: File[]): TrackFile[] {
  if (files.length == 0) return [];

  files.sort((f1, f2) => f1.name.localeCompare(f2.name));

  let runningLcp = null; // "abc "
  let runningStems: StemFile[] = [{ name: files[0].name, file: files[0] }]; // "abc "
  let trackFiles: TrackFile[] = [];
  files.slice(0, files.length - 1).forEach((file1, idx) => {
    let file2 = files[idx + 1];
    let thisLcp = lcp([runningLcp || file1.name, file2.name]); // "abc "

    let endsWithSpace = thisLcp.endsWith(" ") || thisLcp.endsWith("_");
    let lastSpaceIdx = thisLcp.lastIndexOf(" ") === -1 ? thisLcp.lastIndexOf("_") : thisLcp.lastIndexOf(" ");
    if (!endsWithSpace) {
      thisLcp = thisLcp.slice(0, lastSpaceIdx);
    }
    endsWithSpace = thisLcp.endsWith(" ") || thisLcp.endsWith("_");
    console.log(`${file1.name}, ${file2.name}\t(lcp: ${thisLcp}\trunningLcp: ${runningLcp})`);

    // if (!endsWithSpace || thisLcp === "" || (runningLcp !== null && thisLcp.length < runningLcp.length)) {
    if (true) {
      // group ended because no more lcp, or not the first pair and lcp got shorter
      let newTrackFile: TrackFile = composeTrackFile(runningLcp || file1.name, runningStems);

      trackFiles.push(newTrackFile);
      runningLcp = null;
      runningStems = [{ name: file2.name, file: file2 }];
    } else {
      // got a lcp and either first pair or lcp stayed
      runningLcp = thisLcp;
      runningStems.push({ name: file2.name, file: file2 });
    }
  });

  trackFiles.push(composeTrackFile(runningLcp || files[files.length - 1].name, runningStems));

  return trackFiles;
}

// contains FileUpload objects for uploading files and FirestoreTrack for completed uploads
export type FileUploadMap = { [fileName: string]: UITrack };

type StorageUploadValue = {
  uploadFiles: (files: File[]) => any;
  fileUploads: FileUploadMap;
  cancelUpload: (fileUpload: FileUpload) => Promise<any>;
  addUploadListener: (cb: (track: UITrack) => any) => number;
  // removeUploadListener: (listenerId: number) => any
  updateFileUpload: (track: UITrack) => any;
  setPack: (pack: Pack) => any;
};

function getFileExtension(fname: string): string {
  return fname.split(".").pop();
}

export type Stem = { name: string; storagePath: string; waveform?: number[] };
export type UIStem = Stem & { muted?: boolean };
function createUploadTasks(
  user: User,
  path: string,
  trackId: string,
  trackFile: TrackFile
): [UploadTask[], string, Stem[]] {
  const storagePath = `${getUserStoragePath(user)}${path}/`;
  let tasks = [];
  let mainFileStoragePath: string;
  let stemObjects: Stem[] = [];
  if (trackFile.mainFile) {
    mainFileStoragePath = `${storagePath}${trackId}.${getFileExtension(trackFile.mainFile.name)}`;
    tasks.push(uploadBytesResumable(ref(storage, mainFileStoragePath), trackFile.mainFile));
  }

  if (trackFile.stems) {
    trackFile.stems.forEach((stem) => {
      let stemObject: Stem = {
        name: stem.name,
        storagePath: `${storagePath}${randomString(128)}.${getFileExtension(stem.file.name)}`,
      };

      tasks.push(uploadBytesResumable(ref(storage, stemObject.storagePath), stem.file));
      stemObjects.push(stemObject);
    });
  }

  return [tasks, mainFileStoragePath, stemObjects];
}

export const _useStorageUpload = (path: string): StorageUploadValue => {
  const { user } = useAuth();

  const [fileUploads, setFileUploads] = useState<FileUploadMap>({});
  const [pack, setPack] = useState<Pack>(null);

  // const [startListeners, setStartListeners] = useState({})
  const [uploadListener, setUploadListener] = useState(null);
  const addUploadListener = useCallback(
    (cb: (track: UITrack) => any) => {
      // const listenerId = Object.keys(startListeners).length
      // setStartListeners(oldListeners => ({...oldListeners, [listenerId]: cb}))
      setUploadListener(() => cb);
      // return listenerId
      return 0;
    },
    [setUploadListener]
  );
  // const removeUploadListener = (listenerId: number) => setStartListeners(oldListeners => {let listeners = {...oldListeners}; delete listeners[listenerId]; listeners})

  const cancelUpload = async (fileUpload: FileUpload) => {
    fileUpload?.tasks?.forEach((uT) => uT?.cancel());
    setFileUploads((old) => {
      let newFileUploads = { ...old };
      delete newFileUploads[fileUpload?.file?.name || fileUpload.track.title];
      return newFileUploads;
    });
  };
  // todo: map filenames to in-memory ids to allow uploading same-named files
  const uploadFiles = (files: File[]) => {
    // setFileUploads(files.reduce((acc, f, idx) => (acc[f.name] = { file: f, progress: 0, order: idx }, acc), {}));
    let trackFiles = groupFiles(files);

    trackFiles.forEach((trackFile, idx) => {
      // todo: think through security, set up cloud storage rules
      const trackId = randomString(128);
      const [uploadTasks, mainFileStoragePath, stems] = createUploadTasks(user, path, trackId, trackFile);

      // compute Track object (contains key, bpm) before starting the upload
      // that way, user can enter track data while upload is running

      let [track, cleanFname]: [Track, string] = fileNameToTrack(trackFile.name, user?.settings?.analyze_files);

      track = {
        ...track,
        stems,
        id: trackId,
        path: `users/${user.uid}/tracks/${trackId}`,
        creator: authUserToFsUser(user),
      };
      track.title = getPathWithoutExtension(cleanFname);
      if (user?.settings?.analyze_files === false) {
        track.analyze = false;
      } else {
        track.analyze_status = "queued";
      }

      if (mainFileStoragePath) track.url = mainFileStoragePath;
      track.uploadDate = new Date();
      uploadListener({ track });

      setFileUploads((old) => ({
        ...old,
        [trackFile.name]: {
          file: trackFile.mainFile,
          progress: 0,
          track,
          tasks: uploadTasks,
        },
      }));

      const tasksLeft = new Set(uploadTasks.map((_, i) => i));

      uploadTasks.forEach((uploadTask, uploadTaskIdx) =>
        uploadTask.on(
          "state_changed",
          (snapshot) => {
            const progress =
              (sum(uploadTasks.map((uT) => uT.snapshot.bytesTransferred)) /
                sum(uploadTasks.map((uT) => uT.snapshot.totalBytes))) *
              100;
            // console.log(f.name + ' upload is ' + progress + '% done');
            setFileUploads((old) => ({ ...old, [trackFile.name]: { ...old[trackFile.name], progress } }));
          },
          (error) => {
            // Handle unsuccessful uploads
            if (error.code == "storage/canceled") return;
            console.error(error);
            setFileUploads((old) => ({ ...old, [trackFile.name]: { ...old[trackFile.name], error } }));
          },
          () => {
            tasksLeft.delete(uploadTaskIdx);
            if (tasksLeft.size === 0) {
              // Handle successful uploads on complete
              // For instance, get the download URL: https://firebasestorage.googleapis.com/...
              const fileUploadToFirestoreTrack = async (fileUploads: FileUploadMap) => {
                let track = { ...fileUploads[trackFile.name].track };
                delete track.file;
                if (!track.url) delete track.url;
                if (track.analyze !== false) track.analyze_status = "queued";
                let trackRef = await addTrack(user.uid, trackId, track);

                setFileUploads(
                  (old) =>
                    old[trackFile.name].track && {
                      ...old,
                      [trackFile.name]: {
                        track: old[trackFile.name].track,
                        ref: trackRef,
                        file: { name: trackFile.name },
                        justUploaded: true,
                      },
                    }
                );
              };

              setFileUploads((fileUploads) => {
                fileUploadToFirestoreTrack(fileUploads);
                return fileUploads;
              });
            }
          }
        )
      );
    });
  };

  const updateFileUpload = (track: UITrack) => {
    setFileUploads((old) => {
      // @ts-ignore
      Object.entries(old).forEach(
        ([key, val]) => val?.ref && val.track.path === track.track.path && (old[key] = track)
      );
      return old;
    });
  };
  return { uploadFiles, fileUploads, setPack, cancelUpload, addUploadListener, updateFileUpload };
};
function composeTrackFile(trackTitle: string, runningStems: StemFile[]) {
  let newTrackFile: TrackFile = {
    name: trim(trim(trackTitle, " "), "_"),
  };

  if (runningStems.length === 1) {
    newTrackFile.mainFile = runningStems[0].file;
  } else {
    // remove prefix from stems
    runningStems = runningStems.map(({ file, name }) => ({ file, name: name.replace(trackTitle, "") }));

    // extract master stem to mainFile property of new TrackFile if exists
    let masterStemIdx = runningStems.findIndex((stem) => stem.name.match(/master/gi));
    if (masterStemIdx !== -1) {
      newTrackFile.mainFile = runningStems[masterStemIdx].file;
      runningStems.splice(masterStemIdx, 1);
    }

    newTrackFile.stems = runningStems;
  }
  return newTrackFile;
}

export function stringToKey(keyString: string) {
  if (!keyString?.length) return null;
  let rawKey = keyString.replace(" major", "").replace(" minor", "m");
  let key = rawKey[0].toLocaleUpperCase();
  let mode = "major";
  if (!"cdefgab".includes(key.toLocaleLowerCase())) throw new Error(`${key} is not a note`);

  if (rawKey.length > 1) {
    if (rawKey[1] === "#" || rawKey[1] === "b") {
      key += rawKey[1];
      if (rawKey.length > 2) {
        if (rawKey[2] === "m") mode = "minor";
        else throw new Error("That's not a key");
      }
    } else if (rawKey[1] === "m") mode = "minor";
    else throw new Error("That's not a key");
  }
  return { key: key, mode };
}

const initVal: StorageUploadValue = {
  uploadFiles: null,
  fileUploads: {},
  cancelUpload: null,
  addUploadListener: null,
  setPack: null,
  updateFileUpload: null,
};
const TrackUploadContext = createContext(initVal);

export function TrackUploadContextProvider({ children }) {
  const storageUpload = _useStorageUpload(`tracks`);
  return <TrackUploadContext.Provider value={storageUpload}>{children}</TrackUploadContext.Provider>;
}

export const useStorageUpload = () => useContext(TrackUploadContext);
